import {AiBadge} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import {isEmptyAiValue} from '../utils';

import type {AiFieldAccessor, AiSuggestion} from '../types';

export interface UseFieldAiPropsParams {
  aiFields: Map<string, AiSuggestion>;
  isProcessing: boolean;
  markFieldModified: (field: string) => void;
  /** Reads a field's current form value — e.g. `form.getValues`. */
  getValue: (field: string) => unknown;
}

/**
 * Centralizes the per-field AI wiring duplicated across every AI-extractable form section:
 * a corner badge (generating while AI is filling the field, filled once a suggestion lands),
 * a skeleton flag for the field's value area, and an onChange hook that dismisses the badge
 * once the user edits an auto-filled field.
 */
export function useFieldAiProps({
  aiFields,
  isProcessing,
  markFieldModified,
  getValue
}: UseFieldAiPropsParams): (field: string) => AiFieldAccessor {
  const {t} = useTranslation('common');

  return (field: string) => {
    const suggestion = aiFields.get(field);
    const hasSuggestion = !!suggestion && !suggestion.dismissed;
    // AI only ever fills empty fields — a field that already has a value is never a candidate,
    // so it shouldn't show the "generating" indicator even while the rest of the form is.
    const hasExistingValue = !isEmptyAiValue(getValue(field));
    const aiGenerating = isProcessing && !hasSuggestion && !hasExistingValue;

    const aiIndicator = aiGenerating ? (
      <AiBadge state="generating">{t('aiExtraction.field.generating')}</AiBadge>
    ) : hasSuggestion ? (
      <AiBadge state="filled">{t('aiExtraction.field.filled')}</AiBadge>
    ) : undefined;

    const onAiChange = () => {
      if (aiFields.has(field)) markFieldModified(field);
    };

    return {aiIndicator, aiGenerating, onAiChange};
  };
}
